import { useState } from 'react';
import { X, Loader2, RotateCcw } from 'lucide-react';
import type { ComputedLine, ComputedCell } from '../../types/lohnBuchungsbeleg';
import { lohnBuchungsbelegApi, fmtMoney } from '../../services/lohnBuchungsbelegApi';

interface Props {
    runId: number;
    line: ComputedLine;
    cell: ComputedCell;
    onClose: () => void;
    onChanged: () => void;
}

/**
 * Manually override a single computed cell. A comment is MANDATORY. The override
 * is stored against the run (month) only — it never carries into the next month.
 */
export function OverrideModal({ runId, line, cell, onClose, onChanged }: Props) {
    const isOverridden = cell.override !== null;
    // Prefilled in Rappen: the bureau's 4-dp cells would otherwise fail the 2-dp rule below.
    const [value, setValue] = useState<string>(String(Math.round((cell.final ?? 0) * 100) / 100));
    const [comment, setComment] = useState<string>(isOverridden ? (cell.comment ?? '') : '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const numeric = Number(value.replace(/'/g, '').replace(',', '.'));
    // Bookings are in whole Rappen — an override with more than 2 decimals is a typo.
    const tooManyDecimals = isFinite(numeric) && Math.abs(numeric * 100 - Math.round(numeric * 100)) > 1e-6;
    const valid = comment.trim().length > 0 && comment.trim().length <= 1000 && isFinite(numeric) && !tooManyDecimals;

    const save = async () => {
        if (!valid) return;
        setSaving(true);
        setError(null);
        try {
            await lohnBuchungsbelegApi.setOverride(runId, line.templateLineId, cell.costCenterCode, numeric, comment.trim());
            onChanged();
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const revert = async () => {
        setSaving(true);
        setError(null);
        try {
            await lohnBuchungsbelegApi.deleteOverride(runId, line.templateLineId, cell.costCenterCode);
            onChanged();
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Zurücksetzen fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-slate-800">Wert überschreiben</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-3 px-4 py-4 text-sm">
                    <div className="rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        <div><span className="font-medium">{line.beschreibung}</span></div>
                        <div className="mt-0.5">Soll {line.soll ?? '–'} / Haben {line.haben ?? '–'} · KST {cell.costCenterCode}</div>
                        <div className="mt-0.5">Berechnet: <span className="font-mono">{fmtMoney(cell.computed, false)}</span></div>
                    </div>

                    <label className="block">
                        <span className="text-xs font-medium text-slate-600">Neuer Wert</span>
                        <input
                            type="text"
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-sm focus:border-amber-400 focus:outline-none"
                        />
                        {tooManyDecimals && <span className="mt-0.5 block text-[11px] text-rose-600">Höchstens 2 Nachkommastellen (Rappen).</span>}
                    </label>

                    <label className="block">
                        <span className="text-xs font-medium text-slate-600">Kommentar (Pflicht)</span>
                        <textarea
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            rows={3}
                            maxLength={1000}
                            placeholder="Warum wird dieser Wert manuell angepasst?"
                            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-amber-400 focus:outline-none"
                        />
                        <span className="mt-0.5 block text-right text-[10px] text-slate-400">{comment.trim().length}/1000</span>
                    </label>

                    {error && <div className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">{error}</div>}
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
                    {isOverridden ? (
                        <button
                            onClick={revert}
                            disabled={saving}
                            className="inline-flex items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Zurücksetzen
                        </button>
                    ) : <span />}
                    <div className="flex gap-2">
                        <button onClick={onClose} className="rounded px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700">Abbrechen</button>
                        <button
                            onClick={save}
                            disabled={!valid || saving}
                            className="inline-flex items-center gap-1 rounded bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Speichern
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
